import { useLazyQuery } from '@apollo/client/react';
import { PipelineStatusBadge } from '@optools/components/pipelines/pipeline-status-badge';
import { TeamSpot, TeamSpotProps } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import { BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import { PipelineStatus, Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { Database, RefreshCcw, Zap } from 'lucide-react';

import { PipelineAlertsDialog } from '../pipeline-alerts-dialog';
import { PipelineControlButton } from '../pipeline-control-button';

export interface PipelineDetailHeaderProps {
  date: string;
  gamePk: number;
  mediaId: string;
  pipeline: BtcSchedulePipeline;
  team: TeamSpotProps['team'];
}
interface PipelineInfo {
  id: number;
  region: Region;
  status: PipelineStatus | null | undefined;
}
const PipelineInfo = ({ id, region, status }: PipelineInfo) => {
  return (
    <div className="flex items-center gap-1">
      <h3 className="w-16 text-xs font-bold">{region}</h3>
      <Badge variant="secondary">
        <Database />
        {id}
      </Badge>
      {!!status && <PipelineStatusBadge status={status} />}
    </div>
  );
};

export function PipelineDetailHeader({
  date,
  gamePk,
  mediaId,
  pipeline,
  team,
}: PipelineDetailHeaderProps) {
  const [refreshPipelines, { called, loading, refetch }] = useLazyQuery(BTC_GAME_PIPELINE, {
    fetchPolicy: 'network-only',
  });

  const { alerts, feedType, id, regionalPipelines, status, streamState } = pipeline;

  const pipelineAlerts =
    regionalPipelines?.length > 0
      ? regionalPipelines
          .map((regionalPipeline) =>
            regionalPipeline.alerts.map((alert) => ({
              ...alert,
              region: regionalPipeline.region,
            })),
          )
          .flat()
      : alerts;

  return (
    <header className="flex items-center justify-between gap-8">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-4 py-2">
          <div className="flex items-center gap-2">
            <TeamSpot showAbbreviation={false} size={48} team={team} />
            <h1 className="text-xl font-bold">{feedType}</h1>
          </div>
          <div className="flex items-center gap-2 [&_span]:self-center">
            {pipeline.regionalPipelines?.length > 0 ? (
              <Card className="flex flex-col gap-2 p-2">
                {pipeline.regionalPipelines.map(({ id, region, status }) => {
                  return <PipelineInfo key={id} id={id} region={region} status={status} />;
                })}
              </Card>
            ) : (
              <>
                <Badge variant="secondary">
                  <Database />
                  {id}
                </Badge>
                <PipelineStatusBadge status={status} />
              </>
            )}
            <Badge variant="secondary">
              <Zap />
              {streamState}
            </Badge>
            {pipeline.status !== PipelineStatus.ARCHIVED ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Refresh Pipeline"
                    className="rounded-full"
                    data-testid="refresh-pipeline"
                    disabled={loading}
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      void (called
                        ? refetch()
                        : refreshPipelines({ variables: { gamePk, scheduleDate: date } }))
                    }>
                    <RefreshCcw className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent align="center" side="right">
                  Refresh Pipeline
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <PipelineAlertsDialog alerts={pipelineAlerts} mediaId={mediaId} />
          {/* Pipeline Control Button(s) */}
          {status !== PipelineStatus.ARCHIVED ? (
            <PipelineControlButton
              gamePk={gamePk}
              mediaId={mediaId}
              navigateOnDeprovision={true}
              pipelineId={id}
              status={status}
            />
          ) : null}
        </div>
      </div>
    </header>
  );
}
